import { z } from "zod";

// NOTE (Ezra, 2026-07-30): Keep the Site Builder agent view providers aligned
// with the website-builder miniapp so the model cannot invent provider values.
export const scriptProviderSchema = z.enum([
  "GA4",
  "GoogleTagManager",
  "Meta",
  "TikTok",
  "Pinterest",
  "Reddit",
  "Snapchat",
  "Hotjar",
  "MicrosoftClarity",
  "Contentsquare",
  "Triplewhale",
  "Segment",
  "Northbeam",
  "Cookiebot",
  "Elevar",
  "Gorgias",
  "Converge",
]);

export type ScriptProvider = z.infer<typeof scriptProviderSchema>;

export type ScriptProviderMetadata = {
  title: string;
  image: string;
  description: string;
};

// Keyed by `ScriptProvider` so a new provider fails to compile until it is
// described here (REPL-30181).
export const SCRIPT_PROVIDER_METADATA: Record<
  ScriptProvider,
  ScriptProviderMetadata
> = {
  GA4: {
    title: "Google Analytics 4",
    image: "/images/integration/logos/googleanalytics.svg",
    description: "Page views, engagement, and e-commerce events",
  },
  GoogleTagManager: {
    title: "Google Tag Manager",
    image: "/images/integration/logos/googletagmanager.svg",
    description: "Manage all your tags in one container",
  },
  Meta: {
    title: "Meta Pixel",
    image: "/images/integration/logos/meta.svg",
    description: "Track Facebook and Instagram ad conversions",
  },
  TikTok: {
    title: "TikTok Pixel",
    image: "/images/integration/logos/tiktok.svg",
    description: "Measure TikTok ad campaign performance",
  },
  Pinterest: {
    title: "Pinterest Tag",
    image: "/images/integration/logos/pinterest.svg",
    description: "Track Pinterest ad conversions and audiences",
  },
  Reddit: {
    title: "Reddit Pixel",
    image: "/images/integration/logos/reddit.svg",
    description: "Measure Reddit ad campaign performance",
  },
  Snapchat: {
    title: "Snapchat Pixel",
    image: "/images/integration/logos/snapchat.svg",
    description: "Track Snapchat ad conversions and audiences",
  },
  Hotjar: {
    title: "Hotjar",
    image: "/images/integration/logos/hotjar.svg",
    description: "Heatmaps and session recordings",
  },
  MicrosoftClarity: {
    title: "Microsoft Clarity",
    image: "/images/integration/logos/microsoftclarity.png",
    description: "Free heatmaps and behavior analytics",
  },
  Contentsquare: {
    title: "Contentsquare",
    image: "/images/integration/logos/contentsquare.svg",
    description: "Digital experience analytics and zoning",
  },
  Triplewhale: {
    title: "Triple Whale",
    image: "/images/integration/logos/triplewhale.svg",
    description: "E-commerce attribution and true ROAS",
  },
  Segment: {
    title: "Segment",
    image: "/images/integration/logos/segment.svg",
    description: "Collect events and route to downstream tools",
  },
  Northbeam: {
    title: "Northbeam",
    image: "/images/integration/logos/northbeam.svg",
    description: "Marketing attribution and multi-touch analytics",
  },
  Cookiebot: {
    title: "Cookiebot",
    image: "/images/integration/logos/cookiebot.svg",
    description: "Cookie consent banner and script blocking (GDPR/CCPA)",
  },
  Elevar: {
    title: "Elevar",
    image: "/images/integration/logos/elevar.png",
    description: "Server-side conversion tracking and GTM data layer",
  },
  Gorgias: {
    title: "Gorgias Chat",
    image: "/images/integration/logos/gorgias.svg",
    description: "Live chat and support bubble from your Gorgias helpdesk",
  },
  Converge: {
    title: "Converge",
    image: "/images/integration/logos/converge.svg",
    description: "E-commerce attribution and conversion tracking",
  },
};
